import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { Slider } from "primereact/slider";

import { Iconify } from "@repo/design-system/components/ui";
import "./RangeSlider.scss";

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

// Rounds to the step's decimals so 6.499999 from the slider reads as 6.5
const roundToStep = (value, step) => {
    const decimals = (String(step).split(".")[1] || "").length;
    return Number(Number(value).toFixed(decimals));
};

/**
 * Select-like trigger showing "min – max"; opens a panel with a two-handle slider,
 * Min / Max inputs and Clear / Done. Value is { min, max } or "" when cleared.
 */
const RangeSlider = ({
    id, value, min = 0, max = 10, step = 0.1, title, placeholder = "Select range",
    isDisabled = false, onChange,
}) => {

    const wrapperRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [draft, setDraft] = useState([min, max]);

    const hasValue = value && value.min !== undefined && value.max !== undefined;

    // Re-seed the draft from the saved value every time the panel opens
    useEffect(() => {
        if (isOpen) setDraft(hasValue ? [value.min, value.max] : [min, max]);
    }, [isOpen]);

    // Close (discarding the draft) on an outside click
    useEffect(() => {
        if (!isOpen) return;
        const handleOutsideClick = (event) => {
            if (!wrapperRef.current?.contains(event.target)) setIsOpen(false);
        };
        document.addEventListener("mousedown", handleOutsideClick);
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, [isOpen]);

    const handleSliderChange = (e) => {
        if (Array.isArray(e.value)) setDraft(e.value.map((v) => roundToStep(v, step)));
    };

    // Typed values are clamped to the bounds and kept on the right side of the other handle
    const handleInputChange = (index) => (e) => {
        const raw = e.target.value;
        setDraft((prev) => {
            const next = [...prev];
            next[index] = raw === "" ? "" : raw;
            return next;
        });
    };

    const handleInputBlur = (index) => () => {
        setDraft((prev) => {
            const next = [...prev];
            const parsed = Number(prev[index]);
            const fallback = index === 0 ? min : max;
            let val = prev[index] === "" || isNaN(parsed) ? fallback : clamp(roundToStep(parsed, step), min, max);
            if (index === 0) val = Math.min(val, Number(next[1]) || max);
            else val = Math.max(val, Number(next[0]) || min);
            next[index] = val;
            return next;
        });
    };

    const handleClear = () => {
        onChange?.("");
        setIsOpen(false);
    };

    const handleDone = () => {
        const lo = clamp(Number(draft[0]) || min, min, max);
        const hi = clamp(Number(draft[1]) || max, min, max);
        onChange?.({ min: Math.min(lo, hi), max: Math.max(lo, hi) });
        setIsOpen(false);
    };

    return (
        <div className="range-slider" ref={wrapperRef} id={id}>
            <button type="button" disabled={isDisabled}
                className={`range-slider-trigger ${isOpen ? "open" : ""} ${hasValue ? "has-value" : ""}`}
                onClick={() => setIsOpen((prev) => !prev)}>
                <span className={hasValue ? "" : "range-slider-placeholder"}>
                    {hasValue ? `${value.min} – ${value.max}` : placeholder}
                </span>
                <Iconify icon={isOpen ? "lucide:chevron-up" : "lucide:chevron-down"} width={16} />
            </button>

            {isOpen && (
                <div className="range-slider-panel">
                    <div className="range-slider-panel-header">
                        <span className="range-slider-title">{title}</span>
                        <span className="range-slider-hint">Drag or type</span>
                    </div>

                    <Slider value={draft.map((v) => Number(v) || 0)} onChange={handleSliderChange}
                        range min={min} max={max} step={step} className="range-slider-track" />
                    <div className="range-slider-bounds">
                        <span>{min}</span>
                        <span>{max}</span>
                    </div>

                    <div className="range-slider-inputs">
                        <label className="range-slider-input">
                            <span>Min</span>
                            <input type="number" min={min} max={max} step={step} value={draft[0]}
                                onChange={handleInputChange(0)} onBlur={handleInputBlur(0)} />
                        </label>
                        <span className="range-slider-to">to</span>
                        <label className="range-slider-input">
                            <span>Max</span>
                            <input type="number" min={min} max={max} step={step} value={draft[1]}
                                onChange={handleInputChange(1)} onBlur={handleInputBlur(1)} />
                        </label>
                    </div>

                    <div className="range-slider-actions">
                        <button type="button" className="range-slider-btn" onClick={handleClear}>Clear</button>
                        <button type="button" className="range-slider-btn primary" onClick={handleDone}>Done</button>
                    </div>
                </div>
            )}
        </div>
    );
};

RangeSlider.propTypes = {
    id: PropTypes.string,
    value: PropTypes.oneOfType([
        PropTypes.shape({ min: PropTypes.number, max: PropTypes.number }),
        PropTypes.string,
    ]),
    min: PropTypes.number,
    max: PropTypes.number,
    step: PropTypes.number,
    title: PropTypes.string,
    placeholder: PropTypes.string,
    isDisabled: PropTypes.bool,
    onChange: PropTypes.func,
};

export default RangeSlider;
